import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useClinicBranding, clinicLogo } from '../context/ClinicBrandingContext';
import { resolveMediaUrl } from '../lib/api';
import { toast } from 'sonner';
import { Lock, Mail } from 'lucide-react';
import { isStaffRole } from '../lib/adminRoles';

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();
  const { info: clinicInfo } = useClinicBranding();
  const logoSrc = resolveMediaUrl(clinicLogo(clinicInfo));
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchParams.get('reason') === 'session_expired') {
      toast.info('Tu sesión expiró por inactividad. Inicia sesión de nuevo para continuar.');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (authLoading) return;
    if (isStaffRole(user?.role)) {
      navigate('/admin', { replace: true });
    }
  }, [authLoading, user, navigate]);

  if (authLoading || isStaffRole(user?.role)) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-redirect">
        <div className="animate-pulse text-muted-foreground">Cargando panel…</div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userData = await login(loginId, password);
      if (!isStaffRole(userData.role)) {
        toast.error('Acceso denegado. Solo administradores.');
        return;
      }
      toast.success('¡Bienvenido!');
      navigate('/admin');
    } catch (error) {
      if (!error.response) {
        toast.error(
          'No se pudo contactar al servidor. Comprueba que backend y frontend estén en ejecución (docker ps).',
        );
        return;
      }
      const message = error.response?.data?.detail || 'Usuario/correo o contraseña incorrectos';
      toast.error(typeof message === 'string' ? message : 'Error de autenticación');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-page">
      <Card className="w-full max-w-md border border-border">
        <CardHeader className="text-center pb-2">
          <img src={logoSrc} alt="Centro Cultural" className="h-16 w-auto mx-auto mb-4 object-contain" />
          <CardTitle className="font-heading text-2xl">Panel de Administración</CardTitle>
          <p className="text-muted-foreground text-sm mt-1">Ingresa tus credenciales</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="loginId" className="flex items-center gap-1 mb-1">
                <Mail className="w-3 h-3" /> Usuario o correo electrónico
              </Label>
              <Input
                id="loginId"
                type="text"
                autoComplete="username"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                required
                autoCapitalize="off"
                className="h-11"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="flex items-center gap-1 mb-1">
                <Lock className="w-3 h-3" /> Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="h-11"
                data-testid="login-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
